import useReadingStats from "api/UserResource/useReadingStats";
import useProfile from "api/UserResource/useProfile";
import { FiBookOpen, FiClock, FiShoppingBag, FiArrowRight } from "react-icons/fi";
import { Link } from "react-router-dom";
import AccountLayout, { AccountSkeleton } from "../AccountLayout";
import AccountPage from "../AccountPage";

const formatReadingTime = (minutes) => {
	if (!minutes) return "0m";
	const hours = Math.floor(minutes / 60);
	const mins = minutes % 60;
	return hours > 0 ? `${hours}h${mins > 0 ? ` ${mins}m` : ""}` : `${mins}m`;
};
const ReadingStats = () => {
	const { data: profile, isLoading } = useProfile();
	const { data: stats, isLoading: loadingStats, isError, refetch } = useReadingStats();
	const booksRead = stats?.books_read ?? 0;
	const booksPurchased = stats?.books_purchased ?? 0;
	const genreBreakdown = stats?.genre_breakdown ?? [];
	const maxCount = Math.max(1, ...genreBreakdown.map((item) => item.count));
	const empty = booksRead === 0 && booksPurchased === 0 && !stats?.reading_time_minutes;
	return (
		<AccountPage>
			<AccountLayout isLoading={isLoading} profile={profile?.data}>
				{loadingStats ? (
					<AccountSkeleton />
				) : isError ? (
					<div className="account-empty" role="alert">
						<FiBookOpen aria-hidden="true" />
						<h2>We couldn’t load your reading activity</h2>
						<p>Please try again in a moment.</p>
						<button type="button" className="account-button" onClick={() => refetch()}>
							Try again
						</button>
					</div>
				) : (
					<>
						<div className="account-section-heading">
							<h2>A chapter at a time</h2>
							<p>Your activity so far, across your Akello library.</p>
						</div>
						<div className="account-stats-grid">
							{[
								{ label: "Books read", value: booksRead, icon: FiBookOpen },
								{
									label: "Time spent reading",
									value: formatReadingTime(stats?.reading_time_minutes),
									icon: FiClock,
								},
								{ label: "Books purchased", value: booksPurchased, icon: FiShoppingBag },
							].map(({ label, value, icon: Icon }) => (
								<div className="account-stat" key={label}>
									<Icon aria-hidden="true" />
									<p className="account-stat-value">{value}</p>
									<p className="account-stat-label">{label}</p>
								</div>
							))}
						</div>
						{empty && (
							<div className="account-empty">
								<FiBookOpen aria-hidden="true" />
								<h2>Your story starts here</h2>
								<p>Open a book and settle in. Your reading activity will appear here as you go.</p>
								<Link to="/books" className="account-button">
									Find your next read <FiArrowRight aria-hidden="true" />
								</Link>
							</div>
						)}
						{genreBreakdown.length > 0 && (
							<section className="account-genre-breakdown">
								<div className="account-section-heading">
									<h2>The genres you explore</h2>
									<p>Your books by genre.</p>
								</div>
								<ul>
									{genreBreakdown.map((item) => (
										<li className="account-genre-row" key={item.genre}>
											<span>{item.genre}</span>
											<div className="account-genre-track" aria-hidden="true">
												<span style={{ width: `${(item.count / maxCount) * 100}%` }} />
											</div>
											<span className="account-helper">
												{item.count} {item.count === 1 ? "book" : "books"}
											</span>
										</li>
									))}
								</ul>
							</section>
						)}
					</>
				)}
			</AccountLayout>
		</AccountPage>
	);
};
export default ReadingStats;
